import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Trash2, Tag, ShoppingCart, CreditCard, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';

export default function CartPage() {
  const { items, removeItem, clearCart, getTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const subtotal = getTotal();
  const total = Math.max(0, subtotal - couponDiscount);

  async function handleApplyCoupon() {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post('/student/validate-coupon', { code: couponCode, cart_total: subtotal });
      setCouponDiscount(data.discount);
      setAppliedCoupon(data);
      toast.success(`Coupon applied! You save Rs.${data.discount}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0);
      setAppliedCoupon(null);
    }
  }

  function removeCoupon() {
    setCouponCode('');
    setCouponDiscount(0);
    setAppliedCoupon(null);
  }

  async function handleCheckout() {
    if (!user) { navigate('/login'); return; }
    if (items.length === 0) return;
    setProcessing(true);

    try {
      // Step 1: Create order (backend creates Razorpay order)
      const orderRes = await api.post('/student/create-order', {
        series_ids: items.map(i => i.id),
        coupon_code: appliedCoupon?.code || ''
      });
      const orderData = orderRes.data;

      // Step 2: Open Razorpay checkout
      if (orderData.razorpay_order_id && orderData.amount_paise > 0) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: orderData.amount_paise,
            currency: orderData.currency || 'INR',
            name: 'Astha Abhyas',
            description: `Purchase ${items.length} test series`,
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              try {
                const verifyRes = await api.post('/student/verify-payment', {
                  order_id: orderData.order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature
                });
                setOrderComplete(true);
                setInvoiceNumber(verifyRes.data.invoice_number);
                clearCart();
                toast.success('Payment successful! Access granted.');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Payment verification failed');
              }
              setProcessing(false);
            },
            prefill: { name: user.name || '', email: user.email || '' },
            theme: { color: '#7551e8' },
            modal: {
              ondismiss: function () { setProcessing(false); toast.info('Payment cancelled'); }
            }
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      } else {
        // Free order - verify directly
        const verifyRes = await api.post('/student/verify-payment', {
          order_id: orderData.order_id,
          razorpay_payment_id: 'free_order',
          razorpay_order_id: '',
          razorpay_signature: ''
        });
        setOrderComplete(true);
        setInvoiceNumber(verifyRes.data.invoice_number);
        clearCart();
        toast.success('Order completed! Access granted.');
        setProcessing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
      setProcessing(false);
    }
  }

  if (orderComplete) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="order-success">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-green-600" />
        </div>
        <h1 className="font-['Outfit'] text-3xl font-semibold text-[#0A0A0A] mb-3">Payment Successful!</h1>
        <p className="text-[#52525B] mb-2">Your purchase is complete. You now have access to all the test series.</p>
        {invoiceNumber && (
          <p className="text-sm text-[#52525B] mb-6">Invoice: <span className="font-mono font-medium text-[#0A0A0A]">{invoiceNumber}</span></p>
        )}
        <div className="flex gap-3 justify-center">
          <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
            Go to Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/')}>
            Continue Browsing
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="cart-page">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} className="mr-2" /> Continue Shopping
      </Button>

      <h1 className="font-['Outfit'] text-3xl font-semibold text-[#0A0A0A] mb-6">
        <ShoppingCart size={28} className="inline mr-3 text-[#7551e8]" />
        Shopping Cart
      </h1>

      {items.length === 0 ? (
        <Card className="p-12 text-center border border-zinc-200" data-testid="empty-cart">
          <ShoppingCart size={48} className="mx-auto mb-4 text-zinc-300" />
          <p className="text-lg font-medium text-[#0A0A0A] mb-2">Your cart is empty</p>
          <p className="text-sm text-[#52525B] mb-4">Browse our test series and add them to your cart</p>
          <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => navigate('/')}>
            Explore Test Series
          </Button>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3" data-testid="cart-items-list">
            {items.map(item => {
              const effectivePrice = (item.discount_price > 0 && item.discount_price < item.price) ? item.discount_price : item.price;
              return (
                <Card key={item.id} className="p-4 border border-zinc-200 flex items-center gap-4" data-testid={`cart-item-${item.id}`}>
                  <div className="w-20 h-14 rounded-lg bg-zinc-100 overflow-hidden shrink-0">
                    <img src={item.thumbnail_url || 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&w=200&h=140&fit=crop'}
                      alt={item.title} className="w-full h-full object-cover"
                      onError={e => { e.target.src = 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&w=200&h=140&fit=crop'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#0A0A0A] truncate">{item.title}</h3>
                    {item.category_name && <span className="text-xs text-[#52525B]">{item.category_name}</span>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-['Outfit'] font-semibold text-lg">&#8377;{effectivePrice}</span>
                      {item.discount_price > 0 && item.discount_price < item.price && (
                        <span className="price-original text-xs">&#8377;{item.price}</span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 shrink-0"
                    onClick={() => removeItem(item.id)} data-testid={`remove-item-${item.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 border border-zinc-200 sticky top-20" data-testid="order-summary">
              <h2 className="font-['Outfit'] font-medium text-lg text-[#0A0A0A] mb-4">Order Summary</h2>

              {/* Coupon */}
              <div className="mb-4">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-green-600" />
                      <span className="text-sm font-medium text-green-700">{appliedCoupon.code}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-green-700">-&#8377;{couponDiscount}</span>
                      <button onClick={removeCoupon} className="text-green-600 hover:text-green-800"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input value={couponCode} onChange={e => setCouponCode(e.target.value)}
                      placeholder="Coupon code" className="text-sm" data-testid="coupon-input"
                      onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()} />
                    <Button variant="outline" size="sm" onClick={handleApplyCoupon} className="shrink-0" data-testid="apply-coupon-btn">
                      Apply
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-[#52525B]">
                  <span>Subtotal ({items.length} items)</span>
                  <span>&#8377;{subtotal}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Coupon Discount</span>
                    <span>-&#8377;{couponDiscount}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-base text-[#0A0A0A]">
                  <span>Total</span>
                  <span>&#8377;{total}</span>
                </div>
              </div>

              <Button className="w-full mt-4 bg-[#7551e8] hover:bg-[#5e3dc7] text-white py-5"
                onClick={handleCheckout} disabled={processing || !user} data-testid="checkout-btn">
                {processing ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Processing Payment...</>
                ) : (
                  <><CreditCard size={16} className="mr-2" /> Pay &#8377;{total}</>
                )}
              </Button>

              {!user && (
                <p className="text-xs text-center text-red-500 mt-2">Please <button onClick={() => navigate('/login')} className="underline">sign in</button> to checkout</p>
              )}
              <p className="text-xs text-center text-zinc-400 mt-2">Secure payment via Razorpay</p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
